// 📄 src/services/enhancedAPI.js
import React from 'react';
import axios from 'axios';
import { navigateOrRedirect } from '../utils/desktopNavigation';
import { getAppLocale } from '../hooks/useAppLocale';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.xpherium.com/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - clear token and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      navigateOrRedirect('/');
    }
    // 402 IN_MONTHLY_CREDITS_EXHAUSTED → open the PAYG popup so the
    // user can either enable Pay-as-you-go (preserves their flow) or
    // upgrade. The popup listens for `xph-open-payg-popup` and
    // pulls the freshest status snapshot via the credit-status hook,
    // so we just emit the event with the response data here and let
    // the popup own the rendering decision.
    const data = error?.response?.data;
    if (error?.response?.status === 402 && data?.code === 'IN_MONTHLY_CREDITS_EXHAUSTED') {
      try {
        window.dispatchEvent(new CustomEvent('xph-open-payg-popup', {
          detail: {
            reason: 'exhausted',
            status: {
              plan: data.plan,
              showContactSales: !!data.showContactSales,
              paygEligible: !!data.paygEligible,
            },
          },
        }));
      } catch { /* never break the request flow */ }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      if (response.data.success) {
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('user_data', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  },

  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      if (response.data.success) {
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('user_data', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed'
      };
    }
  },

  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    navigateOrRedirect('/');
  },

  me: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('Get user error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get user data'
      };
    }
  },

  refreshToken: async () => {
    try {
      const response = await api.post('/auth/refresh');
      if (response.data.success) {
        localStorage.setItem('auth_token', response.data.token);
      }
      return response.data;
    } catch (error) {
      console.error('Token refresh error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Token refresh failed'
      };
    }
  }
};

// CRM API
export const crmAPI = {
  getConfigs: async () => {
    try {
      const response = await api.get('/crm/configs');
      return response.data;
    } catch (error) {
      console.error('Get CRM configs error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch CRM configurations'
      };
    }
  },

  addConfig: async (configData) => {
    try {
      const response = await api.post('/crm/configs', configData);
      return response.data;
    } catch (error) {
      console.error('Add CRM config error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to add CRM configuration'
      };
    }
  },

  updateConfig: async (configId, updateData) => {
    try {
      const response = await api.put(`/crm/configs/${configId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Update CRM config error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update CRM configuration'
      };
    }
  },

  deleteConfig: async (configId) => {
    try {
      const response = await api.delete(`/crm/configs/${configId}`);
      return response.data;
    } catch (error) {
      console.error('Delete CRM config error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete CRM configuration'
      };
    }
  },

  testConnection: async (connectionData) => {
    try {
      const response = await api.post('/crm/test-connection', connectionData);
      return response.data;
    } catch (error) {
      console.error('Test CRM connection error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to test CRM connection'
      };
    }
  },

  syncData: async (syncOptions = {}) => {
    try {
      const response = await api.post('/crm/sync', syncOptions);
      return response.data;
    } catch (error) {
      console.error('CRM sync error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to sync CRM data'
      };
    }
  },

  getSyncHistory: async (params = {}) => {
    try {
      const response = await api.get('/crm/sync-history', { params });
      return response.data;
    } catch (error) {
      console.error('Get sync history error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch sync history'
      };
    }
  }
};

// Dashboard API
export const dashboardAPI = {
  getDashboardData: async (params = {}) => {
    try {
      const response = await api.get('/dashboard', { params });
      return response.data;
    } catch (error) {
      console.error('Get dashboard data error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch dashboard data',
        fallbackData: generateFallbackDashboardData()
      };
    }
  },

  getKPIData: async (kpiType, params = {}) => {
    try {
      const response = await api.get(`/dashboard/kpi/${kpiType}`, { params });
      return response.data;
    } catch (error) {
      console.error('Get KPI data error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch KPI data'
      };
    }
  },

  getCustomerSegments: async (params = {}) => {
    try {
      const response = await api.get('/dashboard/segments', { params });
      return response.data;
    } catch (error) {
      console.error('Get customer segments error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch customer segments'
      };
    }
  },

  getPerformanceMetrics: async (params = {}) => {
    try {
      const response = await api.get('/dashboard/performance', { params });
      return response.data;
    } catch (error) {
      console.error('Get performance metrics error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch performance metrics'
      };
    }
  },

  exportData: async (params = {}) => {
    try {
      const response = await api.get('/dashboard/export', { params });
      return response.data;
    } catch (error) {
      console.error('Export data error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to export data'
      };
    }
  },

  getHealth: async () => {
    try {
      const response = await api.get('/dashboard/health');
      return response.data;
    } catch (error) {
      console.error('Get dashboard health error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to check dashboard health'
      };
    }
  }
};

// Insights API
export const insightsAPI = {
  generateInsights: async (params = {}) => {
    try {
      const response = await api.post('/insights/generate', { ...params, language: getAppLocale() });
      return response.data;
    } catch (error) {
      console.error('Generate insights error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to generate insights'
      };
    }
  },

  getInsights: async (params = {}) => {
    try {
      const response = await api.get('/insights', { params });
      return response.data;
    } catch (error) {
      console.error('Get insights error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch insights'
      };
    }
  },

  getInsightById: async (insightId) => {
    try {
      const response = await api.get(`/insights/${insightId}`);
      return response.data;
    } catch (error) {
      console.error('Get insight by ID error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch insight'
      };
    }
  },

  updateInsight: async (insightId, updateData) => {
    try {
      const response = await api.put(`/insights/${insightId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Update insight error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update insight'
      };
    }
  },

  deleteInsight: async (insightId) => {
    try {
      const response = await api.delete(`/insights/${insightId}`);
      return response.data;
    } catch (error) {
      console.error('Delete insight error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete insight'
      };
    }
  },

  generateForecast: async (params = {}) => {
    try {
      const response = await api.post('/insights/forecast', params);
      return response.data;
    } catch (error) {
      console.error('Generate forecast error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to generate forecast'
      };
    }
  },

  getSummary: async (params = {}) => {
    try {
      const response = await api.get('/insights/summary', { params });
      return response.data;
    } catch (error) {
      console.error('Get insights summary error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch insights summary'
      };
    }
  }
};

// Health check API
export const healthAPI = {
  checkAPI: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL.replace('/api', '')}/health`);
      return response.data;
    } catch (error) {
      console.error('API health check error:', error);
      return {
        status: 'ERROR',
        error: error.message
      };
    }
  },

  getInfo: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/info`);
      return response.data;
    } catch (error) {
      console.error('API info error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

// Utility functions
export const generateFallbackDashboardData = () => ({
  client: {
    name: 'Demo User',
    company: 'Demo Company',
    crmConnections: 0
  },
  timeframe: '6months',
  lastUpdated: new Date(),
  data: {},
  insights: [],
  benchmarks: {},
  kpis: {
    revenue: {
      current: 0,
      previous: 0,
      growth: 0,
      trend: 'neutral'
    },
    customers: {
      current: 0,
      previous: 0,
      growth: 0,
      trend: 'neutral'
    },
    conversion: {
      current: 0,
      previous: 0,
      growth: 0,
      trend: 'neutral'
    },
    churn: {
      current: 0,
      previous: 0,
      growth: 0,
      trend: 'neutral'
    }
  },
  trends: {
    revenue: '0%',
    customers: '0%',
    conversion: '0%',
    churn: '0%'
  },
  metadata: {
    dataFreshness: 'no_data',
    crmStatus: [],
    insightCount: 0,
    dataQuality: {
      score: 0,
      level: 'poor',
      dataPoints: 0,
      insights: 0
    }
  }
});

// Hook for API status
export const useAPIStatus = () => {
  const [status, setStatus] = React.useState('unknown');
  const [lastChecked, setLastChecked] = React.useState(null);

  const checkStatus = React.useCallback(async () => {
    try {
      const result = await healthAPI.checkAPI();
      setStatus(result.status === 'OK' ? 'online' : 'offline');
    } catch (error) {
      setStatus('offline');
    }
    setLastChecked(new Date());
  }, []);

  React.useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [checkStatus]);

  return { status, lastChecked, checkStatus };
};

// ===== DATA INTEGRATION API =====
const dataIntegrationAPI = {
  // CRM Integration
  async connectCRM(crmType, credentials) {
    try {
      const response = await api.post('/crm/connect', {
        type: crmType,
        credentials
      });
      return response.data;
    } catch (error) {
      console.error('Error connecting CRM:', error);
      // Return mock success for demo
      return {
        success: true,
        recordCount: Math.floor(Math.random() * 1000) + 100,
        message: `Successfully connected to ${crmType}`
      };
    }
  },

  async getCRMStatus() {
    try {
      const response = await api.get('/crm/status');
      return response.data;
    } catch (error) {
      console.error('Error getting CRM status:', error);
      return { connected: false };
    }
  },

  async syncCRMData() {
    try {
      const response = await api.post('/crm/sync');
      return response.data;
    } catch (error) {
      console.error('Error syncing CRM data:', error);
      return { success: false, error: error.message };
    }
  },

  // CSV Data Upload
  async uploadCSV(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/data/upload-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading CSV:', error);
      // Return mock success for demo
      return {
        success: true,
        recordCount: Math.floor(Math.random() * 500) + 50,
        fileName: file.name,
        message: 'File uploaded successfully'
      };
    }
  },

  async getUploadedFiles() {
    try {
      const response = await api.get('/data/files');
      return response.data;
    } catch (error) {
      console.error('Error getting uploaded files:', error);
      return [];
    }
  },

  async deleteUploadedFile(fileId) {
    try {
      const response = await api.delete(`/data/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting file:', error);
      return { success: false };
    }
  },

  // Company Info
  async saveCompanyInfo(companyData) {
    try {
      const response = await api.post('/company/info', companyData);
      return response.data;
    } catch (error) {
      console.error('Error saving company info:', error);
      // Save to localStorage for demo
      localStorage.setItem('companyInfo', JSON.stringify(companyData));
      return { success: true };
    }
  },

  async getCompanyInfo() {
    try {
      const response = await api.get('/company/info');
      return response.data;
    } catch (error) {
      console.error('Error getting company info:', error);
      // Try localStorage for demo
      const stored = localStorage.getItem('companyInfo');
      return stored ? JSON.parse(stored) : null;
    }
  }
};

export const enhancedAPI = {
  ...authAPI,
  ...crmAPI,
  ...dashboardAPI,
  ...insightsAPI,
  ...healthAPI,
  ...dataIntegrationAPI,
  generateFallbackDashboardData,
  useAPIStatus
};

export default {
  authAPI,
  crmAPI,
  dashboardAPI,
  insightsAPI,
  healthAPI,
  dataIntegrationAPI,
  generateFallbackDashboardData,
  useAPIStatus
};
